/**
 * Two-finger navigation: pan and zoom together (#40).
 *
 * The original handler only ever computed a zoom ratio from the distance
 * between the two contacts. Sliding both fingers sideways keeps that distance
 * roughly constant, so it produced no movement — and because real fingers
 * splay and rotate slightly as they travel, the distance did not stay
 * *exactly* constant either, so a translation came out as a slow drift in
 * scale. Hence "it tries zooming instead of panning".
 *
 * Two things fix it:
 *
 * 1. **The midpoint drives a pan.** Distance is the scale signal; the point
 *    between the fingers is the position signal, and it was being thrown away.
 * 2. **Zoom has a dead zone.** Until the distance has changed by
 *    {@link PINCH_ZOOM_DEADZONE_PX} the gesture is a pure translation. Past
 *    that it latches on for the rest of the gesture and never re-arms, so a
 *    deliberate pinch is never fought, and the reference distance is rebased
 *    at the moment it engages — the dead zone is spent, not repaid as a jump.
 *
 * Kept browserless, like `wheelZoom.ts` and `touchGesture.ts`, so the feel can
 * be tuned and tested without a device.
 */

/** How far the gap between the fingers must change before the gesture is
 *  treated as a pinch rather than a slide. Roughly the wobble of two fingers
 *  travelling together — large enough that a straight drag never scales,
 *  small enough that an intentional pinch engages immediately. */
export const PINCH_ZOOM_DEADZONE_PX = 12;

/** Matches the clamp in `Viewport`, so a pinch held past the limit does not
 *  bank zoom that has to be unwound before the view responds again. */
export const PINCH_MIN_ZOOM = 0.04;
export const PINCH_MAX_ZOOM = 50;

export interface Contact {
  x: number;
  y: number;
}

export interface PinchState {
  /** Finger separation the current zoom is measured from. */
  refDist: number;
  /** View zoom at that reference distance. */
  refZoom: number;
  lastMidX: number;
  lastMidY: number;
  /** Latched once the dead zone is cleared; never re-arms. */
  zooming: boolean;
}

export interface PinchUpdate {
  /** View-space translation to apply, in px. */
  panDx: number;
  panDy: number;
  /** The absolute zoom to end at, or null while this is a pure translation. */
  zoom: number | null;
  /** View-space point a zoom pivots about — the point between the fingers. */
  pivotX: number;
  pivotY: number;
  next: PinchState;
}

const dist = (a: Contact, b: Contact) => Math.hypot(a.x - b.x, a.y - b.y);
const midX = (a: Contact, b: Contact) => (a.x + b.x) / 2;
const midY = (a: Contact, b: Contact) => (a.y + b.y) / 2;

export function beginPinch(a: Contact, b: Contact, zoom: number): PinchState {
  return {
    refDist: dist(a, b),
    refZoom: zoom,
    lastMidX: midX(a, b),
    lastMidY: midY(a, b),
    zooming: false,
  };
}

export function updatePinch(
  state: PinchState,
  a: Contact,
  b: Contact,
): PinchUpdate {
  const d = dist(a, b);
  const mx = midX(a, b);
  const my = midY(a, b);

  let { refDist, zooming } = state;
  if (!zooming && Math.abs(d - refDist) > PINCH_ZOOM_DEADZONE_PX) {
    zooming = true;
    // Rebase: zoom starts at 1x from here rather than snapping by the width
    // of the dead zone. `refZoom` is still correct — nothing has scaled yet.
    refDist = d;
  }

  const zoom = zooming
    ? Math.min(
        PINCH_MAX_ZOOM,
        Math.max(PINCH_MIN_ZOOM, (state.refZoom * d) / Math.max(1, refDist)),
      )
    : null;

  return {
    panDx: mx - state.lastMidX,
    panDy: my - state.lastMidY,
    zoom,
    pivotX: mx,
    pivotY: my,
    next: {
      refDist,
      refZoom: state.refZoom,
      lastMidX: mx,
      lastMidY: my,
      zooming,
    },
  };
}
